import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { m } from 'motion/react';
import { ArrowRight, Building2, Plus } from 'lucide-react';

import { companies } from '../../api/endpoints';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useT } from '../../i18n/provider';
import { companyStatusTone } from './status';

/** `13 §2`: el panel de todas las empresas que el gerente posee. */
export function GlobalPanel() {
  const t = useT();
  const query = useQuery({ queryKey: ['companies'], queryFn: () => companies.list() });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('nav.brand')}
        title={t('panel.yourCompanies')}
        actions={
          <Link to="/companies/new">
            <Button icon={<Plus />}>{t('panel.newCompany')}</Button>
          </Link>
        }
      />

      {query.isPending ? <SkeletonRows /> : null}
      {query.error ? <ErrorBanner error={query.error} onRetry={() => void query.refetch()} /> : null}

      {query.data?.data.length === 0 ? (
        <EmptyState
          icon={<Building2 />}
          title={t('panel.noCompanies')}
          description={t('panel.noCompaniesHint')}
          action={
            <Link to="/companies/new">
              <Button icon={<Plus />}>{t('panel.createFirst')}</Button>
            </Link>
          }
        />
      ) : null}

      {/*
        El escalonado lo pone la lista y no cada tarjeta: así el retardo sigue
        el orden del DOM, que es el orden en que se lee, y no el orden en que
        React decidió montar los hijos.
      */}
      <m.ul variants={STAGGER} initial="hidden" animate="shown" className="grid gap-3 sm:grid-cols-2">
        {query.data?.data.map((company) => (
          <m.li key={company.id} variants={RISE}>
            <Link
              to="/c/$companySlug"
              params={{ companySlug: company.slug }}
              className="group flex h-full flex-col rounded-lg border border-line bg-raised p-5 shadow-sm transition-[border-color,box-shadow,transform] duration-fast hover:-translate-y-px hover:border-primary/45 hover:shadow-md"
            >
              <div className="flex items-start gap-3">
                <span className="brand-gradient grid size-10 shrink-0 place-items-center rounded-md text-sm font-extrabold text-white shadow-brand">
                  {company.name.charAt(0).toUpperCase()}
                </span>

                <div className="min-w-0 grow">
                  <p className="truncate font-semibold text-ink">{company.name}</p>
                  <p className="truncate font-mono text-xs text-ink-faint">{company.slug}</p>
                </div>

                <ArrowRight
                  className="mt-1 size-4 shrink-0 text-ink-faint transition-transform duration-fast group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden="true"
                />
              </div>

              {company.mission ? (
                <p className="mt-4 line-clamp-2 grow text-sm leading-relaxed text-ink-soft">{company.mission}</p>
              ) : (
                <span className="grow" />
              )}

              <div className="mt-4">
                <Badge tone={companyStatusTone(company.status)} size="sm">
                  {t(`status.company.${company.status}` as 'status.company.active')}
                </Badge>
              </div>
            </Link>
          </m.li>
        ))}
      </m.ul>
    </div>
  );
}
